import {
  loadLocalData,
  saveLocalData,
  STORAGE_BIDS_FILTER_UI_STATE,
  STORAGE_SELECTED_BID_BRANDS_STATE,
  STORAGE_SELECTED_BID_CATEGORY_STATE,
} from '@northladder/utilities';

import { IBidFilterState } from '../types';

/**
 * -----------------------------------------------------------------------------
 * Type definitions for persisted bid filter state subsets.
 * These types define which parts of the filter state are persisted to localStorage.
 */

/**
 * UI state that is persisted to localStorage, including sidebar preferences.
 */
export type IPersistBidsFilterUIState = Pick<
  IBidFilterState,
  'isFiltersDialogPinned'
>;

/**
 * Selected category filter state that can be persisted.
 */
export type IPersistSelectedCategoryState = Pick<
  IBidFilterState,
  'selectedCategoryBidFilter'
>;

/**
 * Selected brands filter state that can be persisted.
 */
export type IPersistSelectedBrandsState = Pick<
  IBidFilterState,
  'selectedBrandBidFilterList'
>;

/**
 * -----------------------------------------------------------------------------
 * Utility functions for persisting and loading bid filter state to/from localStorage.
 * These functions handle secure storage and retrieval of filter preferences to
 * maintain user preferences across sessions.
 */

/**
 * Persists the selected category filter to secure local storage.
 * Used to remember the user's category selection when they return to the bids page.
 *
 * @param targetState - The category filter state to persist, or undefined to clear
 */
export const persistSelectedCategoryFilter = (
  targetState?: IPersistSelectedCategoryState | undefined
) => {
  saveLocalData(STORAGE_SELECTED_BID_CATEGORY_STATE, targetState);
};

/**
 * Retrieves and decrypts the persisted category filter from secure local storage.
 * Returns the previously saved category selection, or undefined if none exists.
 *
 * @returns The persisted category filter state, or undefined if not found
 */
export const loadPersistedSelectedCategoryFilter = () =>
  loadLocalData<IPersistSelectedCategoryState>(
    STORAGE_SELECTED_BID_CATEGORY_STATE
  );

/**
 * Persists the selected brands filter to secure local storage.
 * Used to remember the user's brand selections when they return to the bids page.
 *
 * @param targetState - The brands filter state to persist, or undefined to clear
 */
export const persistSelectedBrandsFilter = (
  targetState: IPersistSelectedBrandsState | undefined
) => {
  saveLocalData(STORAGE_SELECTED_BID_BRANDS_STATE, targetState);
};

/**
 * Retrieves and decrypts the persisted brands filter from secure local storage.
 * Returns the previously saved brand selections, or undefined if none exists.
 *
 * @returns The persisted brands filter state, or undefined if not found
 */
export const loadPersistedSelectedBrandsFilter = () =>
  loadLocalData<IPersistSelectedBrandsState>(STORAGE_SELECTED_BID_BRANDS_STATE);

/**
 * Persists the bids filter UI state (sidebar pinned status, width, resizable preference)
 * to secure local storage. This allows the sidebar state to persist across page reloads.
 *
 * The function merges the provided state with any existing persisted state, allowing
 * partial updates without overwriting other UI preferences.
 *
 * @param targetState - The UI state to persist, including pinned status and sidebar width
 */
export const persistBidsFilterUIState = (
  targetState: IPersistBidsFilterUIState | undefined
) => {
  saveLocalData(STORAGE_BIDS_FILTER_UI_STATE, targetState);
};

/**
 * Retrieves the persisted bids filter UI state from secure local storage.
 * Returns the saved sidebar preferences (pinned status, width, resizable), or undefined
 * if no preferences have been saved yet.
 *
 * @returns The persisted UI state, or undefined if not found
 */
export const loadPersistedBidsFilterUIState = () =>
  loadLocalData<IPersistBidsFilterUIState>(STORAGE_BIDS_FILTER_UI_STATE);
